import { useMemo } from "react";

import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { InfoPopover } from "@/components/cinch/InfoPopover";
import { KpiTile, tonePct } from "@/components/cinch/KpiTile";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { ForwardResponse, NrrResponse } from "../../api";
import { fmtCur, fmtPct, monthLabel } from "../../format";
import { CHART_COLORS } from "./colors";

/** TTM NRR/GDR KPIs + a 24-month actual + 12-month projected combined chart. */
export function TtmRetention({
  data,
  forward,
}: {
  data: NrrResponse;
  forward: ForwardResponse;
}) {
  const nrrTone = tonePct(data.ttm_nrr_pct, 100, 95);
  const gdrTone = tonePct(data.ttm_gdr_pct, 95, 90);

  const chartData = useMemo(() => buildChartData(data, forward), [data, forward]);
  const projectStartMonth = forward.forward_series?.[0]?.month ?? null;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">TTM Revenue Retention</h2>
        <InfoPopover title="TTM Revenue Retention — How it's calculated">
          <p>For every customer with positive MRR <b>12 months ago</b>, compare their MRR today against that starting point.</p>
          <p><b>TTM NRR</b> = Σ end_mrr / Σ start_mrr × 100 (expansion included — can exceed 100%).</p>
          <p><b>TTM GDR</b> = Σ min(end_mrr, start_mrr) / Σ start_mrr × 100 (capped at the starting cohort — no expansion credit).</p>
          <p>This is a true 12-month cohort retention (M-12 anchored), not the product of 12 monthly rates.</p>
        </InfoPopover>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">
        12-month cohort retention from <code>BI.REVENUE_WATERFALL</code>, anchored M-12 → M
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <KpiTile
          label="TTM NRR"
          value={fmtPct(data.ttm_nrr_pct)}
          tone={nrrTone}
          sub={data.cohort_month ? `${monthLabel(data.cohort_month + "-01")} cohort` : ""}
        />
        <KpiTile
          label="TTM GDR"
          value={fmtPct(data.ttm_gdr_pct)}
          tone={gdrTone}
          sub="excludes expansion"
        />
        <KpiTile
          label="Current ARR"
          value={fmtCur(data.current_arr ?? 0)}
          sub="across the active pipeline"
        />
        <KpiTile
          label="Cohort ARR retained"
          value={fmtCur(data.cohort_ending_arr ?? 0)}
          sub={
            data.cohort_starting_arr
              ? `of ${fmtCur(data.cohort_starting_arr)} · ${fmtPct(data.cohort_arr_retained_pct)} · ${data.surviving_count}/${data.cohort_count} customers`
              : "—"
          }
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            TTM GDR &amp; NRR — last 24 months actual + 12 forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 10, right: 16, bottom: 10, left: 0 }}>
                <CartesianGrid stroke={CHART_COLORS.gridLine} strokeDasharray="2 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={2} />
                <YAxis
                  domain={[50, (dataMax: number) => Math.ceil(Math.max(dataMax, 110) / 10) * 10]}
                  allowDataOverflow
                  tick={{ fontSize: 10 }}
                />
                <Tooltip formatter={(value) => (value == null ? "—" : fmtPct(Number(value)))} />
                <ReferenceLine y={100} stroke={CHART_COLORS.muted} strokeDasharray="4 3" />
                <ReferenceLine y={90} stroke={CHART_COLORS.danger} strokeDasharray="4 3" strokeWidth={1.5}
                  label={{ value: "90%", position: "insideRight", fontSize: 10 }} />
                <ReferenceLine y={105} stroke={CHART_COLORS.danger} strokeDasharray="4 3" strokeWidth={1.5}
                  label={{ value: "105%", position: "insideRight", fontSize: 10 }} />
                {projectStartMonth && (
                  <ReferenceLine
                    x={projectStartMonth}
                    stroke={CHART_COLORS.muted}
                    strokeDasharray="3 3"
                    label={{ value: "forecast →", position: "insideTopRight", fontSize: 10 }}
                  />
                )}
                <Area type="monotone" dataKey="proj_gdr_floor" stackId="exp" stroke="none" fill="transparent" />
                <Area type="monotone" dataKey="proj_expansion_gap" stackId="exp" stroke="none"
                  fill={CHART_COLORS.success} fillOpacity={0.10} name="Expansion band" />
                <Line type="monotone" dataKey="nrr_pct" name="NRR (actual)" stroke={CHART_COLORS.success}
                  strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="gdr_pct" name="GDR (actual)" stroke={CHART_COLORS.power}
                  strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="proj_nrr_pct" name="NRR (projected)" stroke={CHART_COLORS.success}
                  strokeWidth={2.5} strokeDasharray="5 4" dot={false} />
                <Line type="monotone" dataKey="proj_gdr_pct" name="GDR (projected)" stroke={CHART_COLORS.power}
                  strokeWidth={2} strokeDasharray="5 4" dot={false} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

interface ChartPoint {
  month: string;
  nrr_pct?: number | null;
  gdr_pct?: number | null;
  proj_nrr_pct?: number | null;
  proj_gdr_pct?: number | null;
  proj_gdr_floor?: number | null;
  proj_expansion_gap?: number | null;
}

function buildChartData(data: NrrResponse, forward: ForwardResponse): ChartPoint[] {
  const actual: ChartPoint[] = (data.series ?? []).map((p) => ({
    month: p.month,
    nrr_pct: p.nrr_pct,
    gdr_pct: p.gdr_pct,
  }));
  const projected: ChartPoint[] = (forward.forward_series ?? []).map((p) => ({
    month: p.month,
    proj_nrr_pct: p.projected_nrr_pct,
    proj_gdr_pct: p.projected_gdr_pct,
    proj_gdr_floor: p.projected_gdr_pct,
    proj_expansion_gap:
      p.projected_nrr_pct != null && p.projected_gdr_pct != null
        ? Math.max(0, p.projected_nrr_pct - p.projected_gdr_pct)
        : 0,
  }));
  // Bridge: duplicate the last actual as the first projected so lines connect.
  const last = actual[actual.length - 1];
  if (last && projected.length) {
    projected.unshift({
      month: last.month,
      proj_nrr_pct: last.nrr_pct,
      proj_gdr_pct: last.gdr_pct,
      proj_gdr_floor: last.gdr_pct,
      proj_expansion_gap: 0,
    });
  }
  const byMonth = new Map<string, ChartPoint>();
  for (const p of actual) byMonth.set(p.month, { ...p });
  for (const p of projected) byMonth.set(p.month, { ...(byMonth.get(p.month) ?? {}), ...p });
  return Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
}
